import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl } from '@angular/forms';
import { dateIsValid, getAge, mapDate } from '../../../helpers/date-helpers';
import { getLtvPercentageValue } from '../../../helpers/format-helpers';
import { BtlType, PropertyType, errorMessages } from '../../../models';
import { IPropertyAndLoan, IUiAffordabilityModel } from '../../../models/affordability-ui';

@Injectable()
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class FormsValidators {
  /* eslint-disable  @typescript-eslint/naming-convention */
  // eslint-disable-next-line max-len
  public static POSTCODE_REGEX = /^(([A-PR-UWYZ0-9][A-HK-Y0-9][AEHMNPRTVXY0-9]?[ABEHMNPRVWXY0-9]? ?){1,2}[0-9][ABD-HJLN-UW-Z]{2}|GIR 0AA)$/i;
  public static MONEY_REGEX = /^\d{1,18}(\.\d{1,2})?$/;
  public static EMAIL_REGEX
    = /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@[*[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+]*/;
  public static TELEPHONE_REGEX = /^([()\-.\\\\/+# 0-9x]|ext|extension)+$/;
  public static DOMAIN_REGEX = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;

  public static date(control: AbstractControl) {
    const { value } = control;
    if (value) {
      const valid = dateIsValid(value);
      return valid ? undefined : { date: true };
    }
    return { date: true };
  }

  public static maxAge(years: number, errorKey = 'ageMax') {
    return function(control: AbstractControl) {
      const { value } = control;
      return value && getAge(mapDate(value)) <= years ? undefined : { [errorKey]: true };
    };
  }

  public static minAge(years: number, errorKey = 'ageMin') {
    return function(control: AbstractControl) {
      const { value } = control;
      return value && getAge(mapDate(value)) >= years ? undefined : { [errorKey]: true };
    };
  }

  public static trueOrFalse(fc: AbstractControl) {
    const valid = fc.value === true || fc.value === false;

    return valid ? null : { required: true };
  }

  public static integer(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value) && Number.isInteger(value);

    return valid
      ? null
      : { integer: true };
  }

  public static number(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value);

    return valid
      ? null
      : { number: true };
  }

  public static numberNotZero(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value) && value > 0;

    return valid
      ? null
      : { numberNotZero: true };
  }

  public static positive(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value) && value >= 0;

    return valid
      ? null
      : { positive: true };
  }

  public static noNumbersValidator(fc: AbstractControl) {
    const containsNumbersRegex = /[0-9]+/;
    if (!containsNumbersRegex.test(fc.value)) {
      return null;
    }

    return { containsNumbers: true };
  }

  public static email(fc: AbstractControl) {
    if (!fc.value) {
      return null;
    }

    const valid = fc.value && FormsValidators.EMAIL_REGEX.test(fc.value);

    return valid
      ? null
      : {
        email: true,
      };
  }

  // https://www.regexpal.com/?fam=104738
  public static telephone(fc: AbstractControl) {
    if (!fc.value) {
      return null;
    }

    const valid = fc.value && FormsValidators.TELEPHONE_REGEX.test(fc.value);

    return valid
      ? null
      : { telephone: true };
  }

  public static minLength(minLength: number) {
    return function(control: AbstractControl) {
      if (!control.value) {
        return null;
      }

      const valid = control.value.toString().length >= minLength;
      return valid
        ? null
        : { minLength: true };
    };
  }

  public static maxLength(maxLength: number) {
    return function(control: AbstractControl) {
      if (!control.value) {
        return null;
      }

      const valid = control.value.toString().length <= maxLength;
      return valid
        ? null
        : { minLength: true };
    };
  }

  public static domain(fc: AbstractControl) {
    if (FormsValidators.DOMAIN_REGEX.test(fc.value)) {
      return null;
    }
    return {
      invalidDomain: true,
    };
  }

  public static postcode(control: AbstractControl) {
    if (!control.value) {
      return null;
    }

    const valid = FormsValidators.POSTCODE_REGEX.test(control.value);
    return valid
      ? null
      : { invalidPostcode: true };
  }

  public static money(control: AbstractControl) {
    const valid = FormsValidators.MONEY_REGEX.test(control.value);
    return valid
      ? null
      : { money: true };
  }

  public static percentage(control: AbstractControl) {
    const value = parseFloat(control.value);
    const valid = !isNaN(value) && value >= 0 && value <= 100;
    return valid
      ? null
      : { percentage: true };
  }

  public static multipleCheckboxRequireAtLeastOne(fa: FormArray) {
    const valid = fa.controls.filter((x) => x.value).length >= 1;
    return valid
      ? null
      : { multipleCheckboxRequireAtLeastOne: true };
  }

  public static multipleCheckboxRequireMoreThanOne(fa: FormArray) {
    const valid = fa.controls.filter((x) => x.value).length > 1;
    return valid
      ? null
      : { multipleCheckboxRequireMoreThanOne: true };
  }

  public static greaterThan(min: number, errorKey: string = 'greaterThan'): any {
    return (control: FormControl) => {
      if (control.value === '' || isNaN(control.value) || control.value <= min) {
        return { [errorKey]: min };
      }
      return null;
    };
  }

  public static greaterThanOnTouched(min: number): any {
    return (control: FormControl) => {
      //field is valid if it was not touched
      if (control.untouched) {
        return null;
      }

      if (control.value === '' || isNaN(control.value) || control.value <= min) {
        return { greaterThan: min };
      }
      return null;
    };
  }

  public static lessThan(max: number): any {
    return (control: FormControl) => {
      if (control.value === '' || isNaN(control.value) || control.value >= max) {
        return { lessThan: max };
      }
      return null;
    };
  }

  /**
   * Performs LTV validation on the given model. Returns an error key (from `ErrorMessages`) if there is a validation
   * error or `null` if there is no error.
   */
  public static loanAmountValidator(propertyAndLoan: IPropertyAndLoan): string {
    if (+propertyAndLoan?.loanAmount < 10000) {
      return 'There is a minimum loan amount of £10,000';
    }
    if (+propertyAndLoan?.loanAmount > 4000000) {
      return 'There is a maximum loan amount of £4,000,000';
    }
    if (propertyAndLoan?.propertyValue || propertyAndLoan?.loanAmount) {
      const ltv: number = getLtvPercentageValue(propertyAndLoan.propertyValue, propertyAndLoan.loanAmount);
      if (ltv > 60) {
        return `The desired amount is larger than our 60% LTV
                but we could consider up £${((+propertyAndLoan.propertyValue / 100) * 60).toLocaleString()}`;
      }
    }
  }

  public static propertyValueValidator(propertyAndLoan: IPropertyAndLoan): keyof typeof errorMessages | null {
    if (+propertyAndLoan?.propertyValue > 4000000) {
      return 'maxPropertyValue';
    } else if (+propertyAndLoan?.propertyValue < 100000 &&
      propertyAndLoan.propertyType === PropertyType.Flat) {
      return 'lowPropertyValueFlat';
    } else if (+propertyAndLoan?.propertyValue < 100000 &&
      propertyAndLoan.propertyType === PropertyType.House && propertyAndLoan.gap) {
      return 'lowPropertyValueHouseGap';
    } else if (+propertyAndLoan?.propertyValue < 70000 &&
      propertyAndLoan.propertyType === PropertyType.House && !propertyAndLoan.gap) {
      return 'lowPropertyValueHouse';
    }
    return null;

  }

  public static ltvValidatorBTLType(value: any, model: IUiAffordabilityModel): keyof typeof errorMessages | null {
    const ltv: number = getLtvPercentageValue(model.propertyAndLoan.propertyValue, model.propertyAndLoan.loanAmount);

    if (value === BtlType.HMO && ltv > 75) {
      return 'ltvPercentBtlHmo';
    } else if (value === BtlType.MultiBlockUnit && ltv > 75) {
      return 'ltvPercentBtlMultiUnit';
    }
    return null;

  }

  public static validationExist(func): boolean {
    const funcStr = func.toString().replace(/\s/g, '');
    return (
      funcStr.indexOf('{required:') > -1 ||
      funcStr.indexOf('{\'required\':') > -1 ||
      funcStr.indexOf('multipleCheckboxesRequireOnlyOneChecked:') > -1 ||
      funcStr.indexOf('multipleCheckboxRequireAtLeastOne:') > -1 ||
      funcStr.indexOf('multipleCheckboxRequireMoreThanOne:') > -1
    );
  }

  public static greaterThanZero(value: any) {
    value = (value || '').replace(/,/g, '');
    return value !== '' && typeof +value === 'number' && !isNaN(value) && +value > 0;
  }
}
